import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "pt-4 flex flex-col flex-1" };
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Button, NavTabs, Tooltip, WidgetShell } from "@eudonet/opal";
import { useContactRelations } from "../../composables/useContactRelations";
import { useHousehold } from "../../composables/useHousehold";
import { usePermissions } from "@/shared/composables/usePermissions";
import ModuleState from "@/shared/components/ModuleState.vue";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import ContactRelationsOrgTab from "./ContactRelationsOrgTab.vue";
import ContactRelationsFoyerTab from "./ContactRelationsFoyerTab.vue";
import CreateRelationModal from "./CreateRelationModal.vue";
import EditRelationModal from "./EditRelationModal.vue";
import DeleteRelationConfirmationModal from "./DeleteRelationConfirmationModal.vue";
import CreateHouseholdModal from "./CreateHouseholdModal.vue";
import EditHouseholdModal from "./EditHouseholdModal.vue";
import DeleteHouseholdModal from "./DeleteHouseholdModal.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ContactRelationsTab',
    props: {
        contactId: {},
        initialSubTab: {}
    },
    emits: ["relations-changed"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        // Relation mutations change the contact's derived Type (Individual ⇄ Professional,
        // OP-5862) — the parent owns the contact aggregate and must refetch it.
        const emit = __emit;
        const { t } = useI18n();
        const { canEditOphub: canEdit, disabledTooltip } = usePermissions();
        const { relations, loading, loadError, load, reload } = useContactRelations(() => props.contactId);
        const { household, loading: householdLoading, loadError: householdLoadError, load: loadHousehold, reload: reloadHousehold, } = useHousehold(() => props.contactId);
        const activeSubTab = ref(props.initialSubTab ?? "organisations");
        const showCreateModal = ref(false);
        // ─── Household modals ──────────────────────────────────────────────────────────
        const showCreateHouseholdModal = ref(false);
        const showEditHouseholdModal = ref(false);
        const showDeleteHouseholdModal = ref(false);
        function onCreateHousehold() {
            showCreateHouseholdModal.value = true;
        }
        function onHouseholdCreated() {
            reloadHousehold();
            reload();
        }
        function onEditHousehold() {
            showEditHouseholdModal.value = true;
        }
        function onDeleteHousehold() {
            showDeleteHouseholdModal.value = true;
        }
        function onHouseholdDeleted() {
            reloadHousehold();
            reload();
        }
        // ─── Edit relation ─────────────────────────────────────────────────────────────
        const editingOrgId = ref(null);
        const showEditModal = ref(false);
        const editingOrg = computed(() => relations.value?.organisations.find((o) => o.id === editingOrgId.value) ?? null);
        function onEditRelation(orgId) {
            editingOrgId.value = orgId;
            showEditModal.value = true;
        }
        // ─── Delete relation ───────────────────────────────────────────────────────────
        const deletingOrgId = ref(null);
        const showDeleteModal = ref(false);
        const deletingOrg = computed(() => relations.value?.organisations.find((o) => o.id === deletingOrgId.value) ?? null);
        function onDeleteRelation(orgId) {
            deletingOrgId.value = orgId;
            showDeleteModal.value = true;
        }
        // ─── Reload helpers ────────────────────────────────────────────────────────────
        function onRelationCreated() {
            reload();
            emit("relations-changed");
        }
        function onRelationUpdated() {
            reload();
            emit("relations-changed");
        }
        function onRelationDeleted() {
            reload();
            emit("relations-changed");
        }
        const showCreateButton = computed(() => {
            if (loading.value || loadError.value || !relations.value)
                return false;
            return activeSubTab.value === "organisations" && (relations.value.organisations?.length ?? 0) > 0;
        });
        const subTabs = computed(() => [
            {
                key: "organisations",
                label: t("contact.detail.relations.subTabs.organisations", {
                    count: relations.value?.organisationCount ?? 0,
                }),
            },
            {
                key: "foyer",
                label: t("contact.detail.relations.subTabs.foyer", {
                    count: relations.value?.householdCount ?? 0,
                }),
            },
        ]);
        onMounted(() => {
            load();
            loadHousehold();
        });
        __expose({ reload });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(WidgetShell), {
                    stretch: "",
                    rounded: "l",
                    header: _ctx.$t('contact.detail.relations.title')
                }, {
                    actions: _withCtx(() => [
                        _createVNode(_unref(Tooltip), {
                            text: _unref(disabledTooltip),
                            disabled: _unref(canEdit),
                            tabindex: !_unref(canEdit) && showCreateButton.value ? 0 : undefined
                        }, {
                            default: _withCtx(() => [
                                _createVNode(_unref(Button), {
                                    variant: "primary",
                                    size: "sm",
                                    "icon-left": "ri-add-line",
                                    label: _ctx.$t('contact.detail.relations.createRelation'),
                                    class: _normalizeClass({ 'invisible pointer-events-none': !showCreateButton.value }),
                                    "aria-hidden": !showCreateButton.value ? 'true' : undefined,
                                    tabindex: !showCreateButton.value ? -1 : undefined,
                                    disabled: !_unref(canEdit),
                                    onClick: _cache[0] || (_cache[0] = ($event) => (showCreateModal.value = true))
                                }, null, 8 /* PROPS */, ["label", "class", "aria-hidden", "tabindex", "disabled"])
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["text", "disabled", "tabindex"])
                    ]),
                    default: _withCtx(() => [
                        (_unref(loading))
                            ? (_openBlock(), _createBlock(ModuleLoading, { key: 0 }))
                            : (_unref(loadError))
                                ? (_openBlock(), _createBlock(ModuleState, {
                                    key: 1,
                                    type: "error",
                                    class: "flex-1",
                                    title: _ctx.$t('contact.detail.relations.loadError.title'),
                                    "cta-icon": "ri-refresh-line",
                                    "cta-label": _ctx.$t('contact.detail.relations.loadError.retry'),
                                    onCta: _unref(reload)
                                }, null, 8 /* PROPS */, ["title", "cta-label", "onCta"]))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                    _createVNode(_unref(NavTabs), {
                                        "model-value": activeSubTab.value,
                                        tabs: subTabs.value,
                                        "overflow-label": _ctx.$t('common.tabs.next'),
                                        "overflow-previous-label": _ctx.$t('common.tabs.previous'),
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (activeSubTab.value = $event))
                                    }, null, 8 /* PROPS */, ["model-value", "tabs", "overflow-label", "overflow-previous-label"]),
                                    _createElementVNode("div", _hoisted_1, [
                                        (activeSubTab.value === 'organisations')
                                            ? (_openBlock(), _createBlock(ContactRelationsOrgTab, {
                                                key: 0,
                                                organisations: _unref(relations)?.organisations ?? [],
                                                "can-edit": _unref(canEdit),
                                                "disabled-tooltip": _unref(disabledTooltip),
                                                onCreateRelation: _cache[2] || (_cache[2] = ($event) => (showCreateModal.value = true)),
                                                onEditRelation: onEditRelation,
                                                onDeleteRelation: onDeleteRelation
                                            }, null, 8 /* PROPS */, ["organisations", "can-edit", "disabled-tooltip"]))
                                            : (activeSubTab.value === 'foyer')
                                                ? (_openBlock(), _createBlock(ContactRelationsFoyerTab, {
                                                    key: 1,
                                                    household: _unref(household),
                                                    loading: _unref(householdLoading),
                                                    "load-error": _unref(householdLoadError),
                                                    "can-edit": _unref(canEdit),
                                                    "disabled-tooltip": _unref(disabledTooltip),
                                                    onCreate: onCreateHousehold,
                                                    onEdit: onEditHousehold,
                                                    onDelete: onDeleteHousehold,
                                                    onRetry: _unref(reloadHousehold)
                                                }, null, 8 /* PROPS */, ["household", "loading", "load-error", "can-edit", "disabled-tooltip", "onRetry"]))
                                                : _createCommentVNode("v-if", true)
                                    ])
                                ], 64 /* STABLE_FRAGMENT */))
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["header"]),
                _createVNode(CreateRelationModal, {
                    modelValue: showCreateModal.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showCreateModal).value = $event)),
                    "contact-id": props.contactId,
                    onCreated: onRelationCreated
                }, null, 8 /* PROPS */, ["modelValue", "contact-id"]),
                (editingOrg.value)
                    ? (_openBlock(), _createBlock(EditRelationModal, {
                        key: 0,
                        modelValue: showEditModal.value,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((showEditModal).value = $event)),
                        "contact-id": props.contactId,
                        organisation: editingOrg.value,
                        onUpdated: onRelationUpdated
                    }, null, 8 /* PROPS */, ["modelValue", "contact-id", "organisation"]))
                    : _createCommentVNode("v-if", true),
                (deletingOrg.value)
                    ? (_openBlock(), _createBlock(DeleteRelationConfirmationModal, {
                        key: 1,
                        modelValue: showDeleteModal.value,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((showDeleteModal).value = $event)),
                        "contact-id": props.contactId,
                        organisation: deletingOrg.value,
                        onDeleted: onRelationDeleted
                    }, null, 8 /* PROPS */, ["modelValue", "contact-id", "organisation"]))
                    : _createCommentVNode("v-if", true),
                _createVNode(CreateHouseholdModal, {
                    modelValue: showCreateHouseholdModal.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((showCreateHouseholdModal).value = $event)),
                    "contact-id": props.contactId,
                    onCreated: onHouseholdCreated
                }, null, 8 /* PROPS */, ["modelValue", "contact-id"]),
                (_unref(household))
                    ? (_openBlock(), _createBlock(EditHouseholdModal, {
                        key: 2,
                        modelValue: showEditHouseholdModal.value,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((showEditHouseholdModal).value = $event)),
                        "contact-id": props.contactId,
                        household: _unref(household),
                        onUpdated: _unref(reloadHousehold)
                    }, null, 8 /* PROPS */, ["modelValue", "contact-id", "household", "onUpdated"]))
                    : _createCommentVNode("v-if", true),
                (_unref(household))
                    ? (_openBlock(), _createBlock(DeleteHouseholdModal, {
                        key: 3,
                        modelValue: showDeleteHouseholdModal.value,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((showDeleteHouseholdModal).value = $event)),
                        "household-id": _unref(household).id,
                        "household-name": _unref(household).name,
                        onDeleted: onHouseholdDeleted
                    }, null, 8 /* PROPS */, ["modelValue", "household-id", "household-name"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
